import { lazy, Suspense, Component } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { PermissionGate } from './components/PermissionGate'
import { QuickEmailProvider } from './components/QuickEmail/QuickEmailProvider'
import { ToastProvider } from './components/ui/Toast'
import { useTeamChatNotifications } from './lib/useTeamChatNotifications'

// ── Stale-deploy resilience ────────────────────────────────────────────────
// The service worker serves index.html network-first but JS chunks cache-first
// (see public/sw.js). A tab left open across a deploy therefore runs OLD
// in-memory code that import()s OLD chunk hashes — which Netlify purges after
// the next deploy. The dynamic import then 404s and, with no error boundary and
// no retry, React renders a blank screen (the long-standing "Settings goes
// blank on interaction" report). lazyWithReload turns that one-time failure
// into a single hard reload, which pulls the fresh index.html + current chunk
// hashes and self-heals. The 10s timestamp guard prevents a reload loop if the
// chunk is genuinely broken — after one reload it rethrows to the boundary.
function lazyWithReload(factory) {
  return lazy(() =>
    factory().catch((err) => {
      try {
        const KEY = 'planos:lastChunkReload'
        const last = Number(sessionStorage.getItem(KEY) || 0)
        if (Date.now() - last > 10000) {
          sessionStorage.setItem(KEY, String(Date.now()))
          window.location.reload()
          return new Promise(() => {})   // hang until the reload takes over
        }
      } catch { /* sessionStorage blocked (private mode) — fall through */ }
      throw err                          // already reloaded once → surface it
    })
  )
}

// Root error boundary. Before this, App.jsx had NO error boundary at all, so any
// uncaught render error (or a chunk error that survived one reload) unmounted
// the whole tree to a blank page. Now it shows a recoverable panel instead.
class RootErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false } }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch(error, info) { console.error('[RootErrorBoundary]', error, info?.componentStack) }
  render() {
    if (!this.state.hasError) return this.props.children
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24,
        textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1a1a1a' }}>Something went wrong.</p>
        <p style={{ margin: 0, fontSize: 13, color: '#666', maxWidth: 360, lineHeight: 1.5 }}>
          The app hit an unexpected error. Reloading usually clears it — your data is safe.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 4, padding: '10px 20px', borderRadius: 8, fontSize: 13,
            fontWeight: 500, background: '#1a1a1a', color: '#fff', border: 'none', cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    )
  }
}

const FeedbackWidget = lazyWithReload(() => import('./components/FeedbackWidget').then(m => ({ default: m.FeedbackWidget })))
const TeamChatFloater = lazyWithReload(() => import('./components/TeamChatFloater').then(m => ({ default: m.TeamChatFloater })))
const HelpFloater = lazyWithReload(() => import('./components/help/HelpFloater').then(m => ({ default: m.HelpFloater })))
const FounderModal = lazyWithReload(() => import('./components/ui/FounderModal').then(m => ({ default: m.FounderModal })))
const OnboardingIntake = lazyWithReload(() => import('./components/onboarding/OnboardingIntake').then(m => ({ default: m.OnboardingIntake })))
const OnboardingChat = lazyWithReload(() => import('./components/onboarding/OnboardingChat').then(m => ({ default: m.OnboardingChat })))
const TeamMemberWelcome = lazyWithReload(() => import('./components/onboarding/TeamMemberWelcome').then(m => ({ default: m.TeamMemberWelcome })))


// One-time cleanup of stale default localStorage keys (prevents bleed from old test events)
try {
  const STALE_LS_KEYS = [
    'planos_client_info_default',
    'planos_tl_default',
    'planos_guests_default',
    'planos_seating_default',
    'planos_phases_default',
    'planos_phase_tasks_default',
  ]
  STALE_LS_KEYS.forEach(key => localStorage.removeItem(key))

  const PURGE_VERSION = 'v20260402'
  if (localStorage.getItem('planos_tasks_purge') !== PURGE_VERSION) {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('planos_phase_tasks_')) {
        localStorage.removeItem(key)
      }
    })
    localStorage.setItem('planos_tasks_purge', PURGE_VERSION)
  }
} catch (_) {}

// Lazy-loaded page components (breaks circular TDZ in bundle)
const Welcome = lazyWithReload(() => import('./pages/onboarding/Welcome').then(m => ({ default: m.Welcome })))
const ConciergeIntake = lazyWithReload(() => import('./pages/onboarding/ConciergeIntake').then(m => ({ default: m.ConciergeIntake })))
const Login = lazyWithReload(() => import('./pages/auth/Login').then(m => ({ default: m.Login })))
const Register = lazyWithReload(() => import('./pages/auth/Register').then(m => ({ default: m.Register })))
const StudioSetup = lazyWithReload(() => import('./pages/auth/StudioSetup').then(m => ({ default: m.StudioSetup })))
const ForgotPassword = lazyWithReload(() => import('./pages/auth/ForgotPassword').then(m => ({ default: m.ForgotPassword })))
const ResetPassword = lazyWithReload(() => import('./pages/auth/ResetPassword').then(m => ({ default: m.ResetPassword })))
const JoinTeam = lazyWithReload(() => import('./pages/auth/JoinTeam').then(m => ({ default: m.JoinTeam })))
const GoogleCallback = lazyWithReload(() => import('./pages/auth/GoogleCallback').then(m => ({ default: m.GoogleCallback })))
// V1 dashboard/home/event/snapshot imports retired 2026-06-06 (T1 audit). The
// V1 routes below now redirect to their V2 equivalents via <RedirectV2>. To
// restore: re-add `Dashboard`, `HomeDashboard`, `EventSnapshot` lazy imports
// and swap their routes back. V1 source files are intentionally left on disk
// in pro/src/pages/{dashboard,event}/ as orphans for rollback safety.
const DashboardV2 = lazyWithReload(() => import('./pages/dashboard/DashboardV2').then(m => ({ default: m.DashboardV2 })))
const HomeDashboardV2 = lazyWithReload(() => import('./pages/dashboard/HomeDashboardV2').then(m => ({ default: m.HomeDashboardV2 })))
const HomeMissionControl = lazyWithReload(() => import('./pages/dashboard/HomeMissionControl').then(m => ({ default: m.HomeMissionControl })))
const EventDetailV2 = lazyWithReload(() => import('./pages/event/EventDetailV2').then(m => ({ default: m.EventDetailV2 })))
const PrintROSV2 = lazyWithReload(() => import('./pages/event/PrintROSV2').then(m => ({ default: m.PrintROSV2 })))
const DayOfModePage = lazyWithReload(() => import('./pages/event/DayOfModePage').then(m => ({ default: m.DayOfModePage })))
const EventSharePage = lazyWithReload(() => import('./pages/share/EventSharePage').then(m => ({ default: m.EventSharePage })))
const PrintBudget = lazyWithReload(() => import('./pages/event/PrintBudget').then(m => ({ default: m.PrintBudget })))
const PrintVendors = lazyWithReload(() => import('./pages/event/PrintVendors').then(m => ({ default: m.PrintVendors })))
const PrintVendorBrief = lazyWithReload(() => import('./pages/event/PrintVendorBrief').then(m => ({ default: m.PrintVendorBrief })))
const PrintFloorPlan = lazyWithReload(() => import('./pages/event/PrintFloorPlan').then(m => ({ default: m.PrintFloorPlan })))
// V1 Upgrade retired 2026-06-06 (T1 audit). Route /upgrade/classic now redirects
// to /upgrade/v2. To restore: re-add lazy import + the V1 route.
const UpgradeV2 = lazyWithReload(() => import('./pages/billing/UpgradeV2').then(m => ({ default: m.UpgradeV2 })))
const BillingSuccess = lazyWithReload(() => import('./pages/billing/Success').then(m => ({ default: m.BillingSuccess })))
const VendorDayOf = lazyWithReload(() => import('./pages/vendor/VendorDayOf').then(m => ({ default: m.VendorDayOf })))
const VendorPortalV2 = lazyWithReload(() => import('./pages/vendor/VendorPortalV2').then(m => ({ default: m.VendorPortalV2 })))
const SharedVendorPortal = lazyWithReload(() => import('./pages/vendor/SharedVendorPortal').then(m => ({ default: m.SharedVendorPortal })))
const PandL = lazyWithReload(() => import('./pages/business/PandL').then(m => ({ default: m.PandL })))
// V1 Lead/Client/Events/ClientDetail retired 2026-06-06 (T1 audit). V1 routes
// redirect to V2 via <RedirectV2>. Source files left on disk as orphans for
// rollback safety; restore by re-adding the lazy imports + V1 routes below.
const LeadDetailV2 = lazyWithReload(() => import('./pages/business/LeadDetailV2').then(m => ({ default: m.LeadDetailV2 })))
const ClientsV2 = lazyWithReload(() => import('./pages/clients/ClientsV2').then(m => ({ default: m.ClientsV2 })))
const EventsListV2 = lazyWithReload(() => import('./pages/events/EventsListV2').then(m => ({ default: m.EventsListV2 })))
const ClientDetailV2 = lazyWithReload(() => import('./pages/clients/ClientDetailV2').then(m => ({ default: m.ClientDetailV2 })))
// Per-event doc hub retired (June 2026) — the event-card pills now open
// /quote/:id · /contract/:id · /invoice/:id directly (matching the client
// page's "Quotes & Invoices" ledger), and per-event payments live in the
// invoice viewer. Redirect any stray/bookmarked hub link to the client page.
function ClientEventDocsRedirect() {
  const { cid } = useParams()
  return <Navigate to={cid ? `/clients/${cid}` : '/clients/v2'} replace />
}
const ClientActivityLog = lazyWithReload(() => import('./pages/clients/ClientActivityLog').then(m => ({ default: m.ClientActivityLog })))
const PlannerVendorsList = lazyWithReload(() => import('./pages/vendors/PlannerVendorsList').then(m => ({ default: m.PlannerVendorsList })))
const PlannerVendorBrief = lazyWithReload(() => import('./pages/vendors/PlannerVendorBrief').then(m => ({ default: m.PlannerVendorBrief })))
const PlannerVenueBrief = lazyWithReload(() => import('./pages/venues/PlannerVenueBrief').then(m => ({ default: m.PlannerVenueBrief })))
const VendorIntakeForm = lazyWithReload(() => import('./pages/vendors/VendorIntakeForm').then(m => ({ default: m.VendorIntakeForm })))
const PrintReferralList = lazyWithReload(() => import('./pages/vendors/PrintReferralList').then(m => ({ default: m.PrintReferralList })))
const PublicReferralList = lazyWithReload(() => import('./pages/vendors/PublicReferralList').then(m => ({ default: m.PublicReferralList })))
const Templates = lazyWithReload(() => import('./pages/templates/Templates').then(m => ({ default: m.Templates })))
const Marketplace = lazyWithReload(() => import('./pages/marketplace/Marketplace').then(m => ({ default: m.Marketplace })))
const Settings = lazyWithReload(() => import('./pages/settings/Settings').then(m => ({ default: m.Settings })))
const FeedbackAdmin = lazyWithReload(() => import('./pages/admin/FeedbackAdmin').then(m => ({ default: m.FeedbackAdmin })))
const AdminPanel = lazyWithReload(() => import('./pages/admin/AdminPanel').then(m => ({ default: m.AdminPanel })))
const BetaCohortPanel = lazyWithReload(() => import('./pages/admin/BetaCohortPanel').then(m => ({ default: m.BetaCohortPanel })))
const AuditBoard = lazyWithReload(() => import('./pages/admin/AuditBoard').then(m => ({ default: m.AuditBoard })))
const PrivacyPolicy = lazyWithReload(() => import('./pages/legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })))
const Terms = lazyWithReload(() => import('./pages/legal/Terms').then(m => ({ default: m.Terms })))
const RSVPForm = lazyWithReload(() => import('./pages/rsvp/RSVPForm').then(m => ({ default: m.RSVPForm })))
const VendorBriefPage = lazyWithReload(() => import('./pages/vendor-brief/VendorBriefPage').then(m => ({ default: m.VendorBriefPage })))
const IntakeForm = lazyWithReload(() => import('./pages/intake/IntakeForm').then(m => ({ default: m.IntakeForm })))
const LeadIntakeForm = lazyWithReload(() => import('./pages/intake/LeadIntakeForm').then(m => ({ default: m.LeadIntakeForm })))
const BankingPage = lazyWithReload(() => import('./pages/banking/BankingPage').then(m => ({ default: m.BankingPage })))
const ProposalBuilder = lazyWithReload(() => import('./pages/proposals/ProposalBuilder').then(m => ({ default: m.ProposalBuilder })))
const ProposalView = lazyWithReload(() => import('./pages/proposals/ProposalView').then(m => ({ default: m.ProposalView })))
const ActivityLog = lazyWithReload(() => import('./pages/activity/ActivityLog').then(m => ({ default: m.ActivityLog })))
const BookingView = lazyWithReload(() => import('./pages/booking/BookingView').then(m => ({ default: m.BookingView })))
const StudioLandingView = lazyWithReload(() => import('./pages/booking/StudioLandingView').then(m => ({ default: m.StudioLandingView })))
const BookingWorkflowPage = lazyWithReload(() => import('./pages/booking/BookingWorkflowPage').then(m => ({ default: m.BookingWorkflowPage })))
const QuoteBuilderPage = lazyWithReload(() => import('./pages/quotes/QuoteBuilderPage').then(m => ({ default: m.QuoteBuilderPage })))
const QuoteViewerPage = lazyWithReload(() => import('./pages/quotes/QuoteViewerPage').then(m => ({ default: m.QuoteViewerPage })))
const ContractReviewPage = lazyWithReload(() => import('./pages/contracts/ContractReviewPage').then(m => ({ default: m.ContractReviewPage })))
const ContractViewerPage = lazyWithReload(() => import('./pages/contracts/ContractViewerPage').then(m => ({ default: m.ContractViewerPage })))
const ClientPortalRouter = lazyWithReload(() => import('./pages/portal/ClientPortalRouter').then(m => ({ default: m.ClientPortalRouter })))
const PortalPrint = lazyWithReload(() => import('./pages/portal/PortalPrint').then(m => ({ default: m.PortalPrint })))
const InvoicePage = lazyWithReload(() => import('./pages/invoices/InvoicePage').then(m => ({ default: m.InvoicePage })))
const InvoicesListV2 = lazyWithReload(() => import('./pages/invoices/InvoicesListV2').then(m => ({ default: m.InvoicesListV2 })))
const EventFinancialSummary = lazyWithReload(() => import('./pages/booking/EventFinancialSummary').then(m => ({ default: m.EventFinancialSummary })))
// Team Chat is now a persistent floating popup (TeamChatFloater, mounted at the
// app root) rather than a full page. The old /team-chat route + TeamChatPage
// lazy import were removed; pages/TeamChatPage.jsx is left on disk (orphaned,
// unimported) for rollback safety.

// ─── V1 → V2 redirect helper ────────────────────────────────────────────────
// Centralized handler for legacy `/v1` and `/classic` route patterns so old
// bookmarks + cached URLs don't 404 after the T1 audit removed the V1
// components from the bundle. Strips `/v1` from the end of pathname (preserving
// :id params naturally since the path itself is the source of truth) and maps
// `/upgrade/classic` → `/upgrade/v2`. Search + hash are preserved so deep
// links survive intact.
//
// Rollback: delete this component, re-add the 7 V1 lazy imports above, and
// restore the original `<Route path="/.../v1" element={<V1 />} />` blocks.
function RedirectV2() {
  const loc = useLocation()
  let target = loc.pathname
  if (target.endsWith('/v1')) target = target.slice(0, -3)
  else if (target === '/upgrade/classic') target = '/upgrade/v2'
  return <Navigate to={target + loc.search + loc.hash} replace />
}

// App-wide Team Chat toast notifier. Renders nothing — just runs the hook so
// a new studio chat message pops a toast no matter what page the user is on.
// Mounted inside ToastProvider (so useToast resolves) + AuthProvider.
function TeamChatNotifier() {
  useTeamChatNotifications()
  return null
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ToastProvider>
        <QuickEmailProvider>
        <FeedbackWidget />
        <FounderModal />
        <Suspense fallback={null}>
          <HelpFloater />
        </Suspense>
        <Suspense fallback={null}>
          <OnboardingIntake />
          <OnboardingChat />
          <TeamMemberWelcome />
        </Suspense>
        <RootErrorBoundary>
        {/* Always-mounted, app-wide listener — inside the boundary so an
            unexpected throw degrades to the recoverable panel, not a blank page. */}
        <TeamChatNotifier />
        {/* Persistent floating Team Chat popup — replaces the old /team-chat
            page. Stays mounted across navigation; renders null on
            login/public/portal views (no studio anchor). */}
        <Suspense fallback={null}>
          <TeamChatFloater />
        </Suspense>
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            {/* Public event share — token-based no-auth view for the
                planner's day-of helpers. Renders read-only timeline +
                contacts + checklist. Validated server-side via
                /.netlify/functions/get-event-share. */}
            <Route path="/share/:token" element={<EventSharePage />} />

            {/* Onboarding */}
            <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
            <Route path="/concierge" element={<ConciergeIntake />} />

            {/* Protected routes — V2 is canonical. V1 paths retired 2026-06-06
                (T1 audit): /home/v1, /dashboard/v1, /event/:id/v1, /lead/:id/v1,
                /events/v1, /clients/v1, /clients/:id/v1, /upgrade/classic all
                redirect to their V2 equivalents via <RedirectV2>. The V1 source
                files remain on disk as orphans for rollback safety. */}
            <Route path="/setup" element={<ProtectedRoute><StudioSetup /></ProtectedRoute>} />
            {/* Mission Control widget home (2026-07-09). The previous dashboard
                stays reachable at /home/v2 as the rollback path. */}
            <Route path="/home" element={<ProtectedRoute><HomeMissionControl /></ProtectedRoute>} />
            <Route path="/home/v3" element={<ProtectedRoute><HomeMissionControl /></ProtectedRoute>} />
            <Route path="/home/v2" element={<ProtectedRoute><HomeDashboardV2 /></ProtectedRoute>} />
            <Route path="/home/v1" element={<RedirectV2 />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardV2 /></ProtectedRoute>} />
            <Route path="/dashboard/v2" element={<ProtectedRoute><DashboardV2 /></ProtectedRoute>} />
            <Route path="/dashboard/v1" element={<RedirectV2 />} />
            <Route path="/event/:id" element={<ProtectedRoute><EventDetailV2 /></ProtectedRoute>} />
            <Route path="/event/:id/v2" element={<ProtectedRoute><EventDetailV2 /></ProtectedRoute>} />
            <Route path="/event/:id/v1" element={<RedirectV2 />} />
            <Route path="/event/:id/ros/v2" element={<ProtectedRoute><PrintROSV2 /></ProtectedRoute>} />
            <Route path="/event/:id/dayof" element={<ProtectedRoute><DayOfModePage /></ProtectedRoute>} />
            <Route path="/event/:id/budget/print" element={<ProtectedRoute><PrintBudget /></ProtectedRoute>} />
            <Route path="/event/:id/vendors/print" element={<ProtectedRoute><PrintVendors /></ProtectedRoute>} />
            <Route path="/event/:id/vendor-brief/print" element={<ProtectedRoute><PrintVendorBrief /></ProtectedRoute>} />
            <Route path="/event/:id/floorplan/print" element={<ProtectedRoute><PrintFloorPlan /></ProtectedRoute>} />
            <Route path="/upgrade" element={<Navigate to="/upgrade/v2" replace />} />
            <Route path="/upgrade/classic" element={<RedirectV2 />} />
            <Route path="/upgrade/v2" element={<ProtectedRoute><UpgradeV2 /></ProtectedRoute>} />
            <Route path="/billing/upgrade" element={<Navigate to="/upgrade/v2" replace />} />
            <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />

            <Route path="/activity" element={<ProtectedRoute><ActivityLog /></ProtectedRoute>} />
            {/* Editorial V2 is now the canonical lead detail page. Keep /v2 as an
                alias so existing deep links from V2 dashboards keep working, and
                expose the legacy V1 page at /v1 for emergency fallback. */}
            <Route path="/lead/:id" element={<ProtectedRoute><LeadDetailV2 /></ProtectedRoute>} />
            <Route path="/lead/:id/v2" element={<ProtectedRoute><LeadDetailV2 /></ProtectedRoute>} />
            <Route path="/lead/:id/v1" element={<RedirectV2 />} />
            <Route path="/events" element={<ProtectedRoute><EventsListV2 /></ProtectedRoute>} />
            <Route path="/events/v2" element={<ProtectedRoute><EventsListV2 /></ProtectedRoute>} />
            <Route path="/events/v1" element={<RedirectV2 />} />
            <Route path="/clients" element={<ProtectedRoute><ClientsV2 /></ProtectedRoute>} />
            <Route path="/clients/v2" element={<ProtectedRoute><ClientsV2 /></ProtectedRoute>} />
            <Route path="/clients/v1" element={<RedirectV2 />} />
            <Route path="/clients/:id" element={<ProtectedRoute><ClientDetailV2 /></ProtectedRoute>} />
            {/* Retired June 2026 — the per-event doc tabs hub was a click-adding
                detour. Pills now deep-link to /quote|/contract|/invoice directly;
                this route just redirects stray/bookmarked links to the client. */}
            <Route path="/client/:cid/event/:eid" element={<ClientEventDocsRedirect />} />
            <Route path="/clients/:id/activity" element={<ProtectedRoute><ClientActivityLog /></ProtectedRoute>} />
            <Route path="/clients/:id/v2" element={<ProtectedRoute><ClientDetailV2 /></ProtectedRoute>} />
            <Route path="/clients/:id/v1" element={<RedirectV2 />} />
            <Route path="/vendors" element={<ProtectedRoute><PlannerVendorsList /></ProtectedRoute>} />
            <Route path="/vendors/referrals" element={<ProtectedRoute><PrintReferralList /></ProtectedRoute>} />
            <Route path="/vendors/:name" element={<ProtectedRoute><PlannerVendorBrief /></ProtectedRoute>} />
            <Route path="/venues/:name" element={<ProtectedRoute><PlannerVenueBrief /></ProtectedRoute>} />
            <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
            <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><PermissionGate permission="settings"><Settings /></PermissionGate></ProtectedRoute>} />
            <Route path="/admin/feedback" element={<ProtectedRoute><FeedbackAdmin /></ProtectedRoute>} />
            <Route path="/admin/beta" element={<ProtectedRoute><BetaCohortPanel /></ProtectedRoute>} />
            <Route path="/admin/audit" element={<ProtectedRoute><AuditBoard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
            <Route path="/banking" element={<ProtectedRoute><PermissionGate permission="banking"><BankingPage /></PermissionGate></ProtectedRoute>} />
            <Route path="/pl" element={<ProtectedRoute><PermissionGate permission="pl"><PandL /></PermissionGate></ProtectedRoute>} />

            {/* Public share routes (no auth needed) */}
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/join/:token" element={<JoinTeam />} />
            {/* Legacy `/client/:token` removed — it was a static Pelican Hill
                mock from early prototyping. Real client portals live at
                `/portal/:token` (ClientPortalRouter). */}
            <Route path="/vendor/:token" element={<VendorDayOf />} />
            <Route path="/vendor/portal/v2/:eventId/:vendorId" element={<VendorPortalV2 />} />
            <Route path="/vendor/portal/:token" element={<VendorPortalV2 />} />
            <Route path="/vendor/portal/event/:eventId" element={<SharedVendorPortal />} />
            <Route path="/rsvp/:token" element={<RSVPForm />} />
            <Route path="/vendor-brief/:token" element={<VendorBriefPage />} />
            <Route path="/intake/demo" element={<IntakeForm />} />
            <Route path="/intake/:token" element={<IntakeForm />} />
            <Route path="/lead-intake/:token" element={<LeadIntakeForm />} />
            <Route path="/vendor-intake/:token" element={<VendorIntakeForm />} />
            <Route path="/r/:token" element={<PublicReferralList />} />

            {/* Proposal routes */}
            <Route path="/proposal/builder" element={<ProtectedRoute><ProposalBuilder /></ProtectedRoute>} />
            <Route path="/proposal/view/:token" element={<ProposalView />} />

            {/* Booking routes (public — no auth) */}
            {/* /book/:token is a per-lead booking funnel (package → contract → pay).
                /studio/:slug is the planner's public inquiry landing page — first
                touch for a visitor, becomes a lead in the planner's account. */}
            <Route path="/book/:token" element={<BookingView />} />
            <Route path="/studio/:slug" element={<StudioLandingView />} />

            {/* Quote workflow routes (planner, protected) */}
            <Route path="/lead/:id/quote" element={<ProtectedRoute><QuoteBuilderPage /></ProtectedRoute>} />
            <Route path="/lead/:id/booking" element={<ProtectedRoute><BookingWorkflowPage /></ProtectedRoute>} />
            <Route path="/lead/:leadId/contract/:contractId" element={<ProtectedRoute><ContractReviewPage /></ProtectedRoute>} />
            {/* Read-only document archives — work regardless of lead_id (legal retention) */}
            <Route path="/quote/:id"    element={<ProtectedRoute><QuoteViewerPage /></ProtectedRoute>} />
            <Route path="/contract/:id" element={<ProtectedRoute><ContractViewerPage /></ProtectedRoute>} />
            <Route path="/invoices" element={<ProtectedRoute><PermissionGate permission="client_financials"><InvoicesListV2 /></PermissionGate></ProtectedRoute>} />
            <Route path="/invoice/:id" element={<ProtectedRoute><PermissionGate permission="client_financials"><InvoicePage /></PermissionGate></ProtectedRoute>} />
            <Route path="/event/:eventId/financials" element={<ProtectedRoute><EventFinancialSummary /></ProtectedRoute>} />

            {/* Client portal — PUBLIC, no auth */}
            <Route path="/portal/:token" element={<ClientPortalRouter />} />
            <Route path="/portal/:token/print" element={<PortalPrint />} />

            {/* Default landing — editorial V2 is the product */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            {/* Friendly redirects for common URL patterns */}
            <Route path="/leads" element={<Navigate to="/dashboard" replace />} />
            <Route path="/lead" element={<Navigate to="/dashboard" replace />} />
            {/* Catch-all — redirect unknown routes to editorial home */}
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </Suspense>
        </RootErrorBoundary>
        </QuickEmailProvider>
        </ToastProvider>
      </BrowserRouter>
    </AuthProvider>
  )
}
